import { API_BASE, type RequestFn } from '../http';
import type { AttachmentItem, TicketCockpitDetail, TimelineItem } from '../types';

/**
 * Stellt sicher, dass `ticket.sla` inkl. `severity` vorhanden ist (API-Vertrag).
 * Bei abweichenden/älteren Antworten: NONE + Konsole, statt Render-Crash oder stilles Ausblenden.
 */
function normalizeTicketCockpitPayload(res: { ticket: TicketCockpitDetail }): { ticket: TicketCockpitDetail } {
  const t = res.ticket;
  const sla = t.sla;
  if (!sla || sla.severity === undefined || sla.severity === null) {
    console.warn(
      '[api] Ticket-Cockpit: SLA-Block fehlt oder severity fehlt — setze severity=NONE (Ticket-ID)',
      t.id
    );
    return {
      ticket: {
        ...t,
        sla: {
          severity: 'NONE',
          respondBy: sla?.respondBy,
          resolveBy: sla?.resolveBy,
          respondDueInMinutes: sla?.respondDueInMinutes,
          resolveDueInMinutes: sla?.resolveDueInMinutes,
          respondOverdue: sla?.respondOverdue,
          resolveOverdue: sla?.resolveOverdue,
        },
      },
    };
  }
  return res;
}

export function createCockpitApi(request: RequestFn) {
  return {
    async getTicketCockpitDetail(ticketId: number) {
      const res = await request<{ ticket: TicketCockpitDetail }>(`/tickets/${ticketId}/cockpit`);
      return normalizeTicketCockpitPayload(res);
    },

    async getTicketTimeline(ticketId: number) {
      return request<{ timeline: TimelineItem[] }>(`/tickets/${ticketId}/timeline`);
    },

    async getTicketAttachments(ticketId: number) {
      return request<{ attachments: AttachmentItem[] }>(`/tickets/${ticketId}/attachments`);
    },

    getTicketAttachmentDownloadUrl(ticketId: number, docId: number): string {
      return `${API_BASE}/tickets/${ticketId}/attachments/${docId}/download`;
    },

    async uploadTicketAttachment(
      ticketId: number,
      data: { base64: string; filename: string; mimeType?: string }
    ) {
      return request<{ success: true; id: number }>(`/tickets/${ticketId}/attachments`, {
        method: 'POST',
        body: JSON.stringify(data),
      });
    },

    async addTicketNote(ticketId: number, data: { text: string; isInternal: boolean; mentionedUserIds?: string[] }) {
      return request<{ success: true }>(`/tickets/${ticketId}/notes`, {
        method: 'POST',
        body: JSON.stringify(data),
      });
    },

    async addTicketTimeEntry(ticketId: number, data: { minutes: number; notes?: string; workType?: string }) {
      return request<{ success: true }>(`/tickets/${ticketId}/time-entries`, {
        method: 'POST',
        body: JSON.stringify(data),
      });
    },

    async updateTicket(
      ticketId: number,
      data: { summary?: string; description?: string; statusId?: number; priorityId?: number }
    ) {
      return request<{ success: true }>(`/tickets/${ticketId}`, {
        method: 'PATCH',
        body: JSON.stringify(data),
      });
    },

    async getKianAnalysis(ticketId: number | string, source: 'CW' | 'WECLAPP' = 'CW') {
      return request<{ analysis: KianAnalysis | null }>(
        `/tickets/${ticketId}/kian-analysis?source=${source}`
      );
    },

    /**
     * Warum gibt es kein automatisches Briefing? Wird nur abgefragt, wenn keine Analyse
     * vorliegt — liefert `null`, wenn die Diagnose selbst nicht ermittelbar war.
     */
    async getKianAnalysisDiagnosis(ticketId: number | string, source: 'CW' | 'WECLAPP' = 'CW') {
      return request<{ diagnosis: KianAnalysisDiagnosis | null }>(
        `/tickets/${ticketId}/kian-analysis/diagnosis?source=${source}`
      );
    },

    async triggerKianAnalysis(
      ticketId: number | string,
      data: {
        source: 'CW' | 'WECLAPP';
        mode: 'STANDARD' | 'DEEP';
        triggerComment?: string;
        autoTrigger?: boolean;
        ticketEnteredAt?: string;
      }
    ) {
      return request<{ analysis: KianAnalysis }>(`/tickets/${ticketId}/kian-analysis`, {
        method: 'POST',
        kianUsageInline: true,
        body: JSON.stringify(data),
      });
    },

    async postKianAnalysisToCw(ticketId: number | string, analysisId: string) {
      return request<{ success: true }>(`/tickets/${ticketId}/kian-analysis/${analysisId}/post`, {
        method: 'POST',
      });
    },
  };
}

export interface KianAnalysis {
  id: string;
  ticketSource: 'CW' | 'WECLAPP';
  ticketId: string;
  mode: 'STANDARD' | 'DEEP';
  analysisText: string;
  contextMeta: Record<string, unknown> | null;
  triggerComment: string | null;
  autoTriggered: boolean;
  postedAt: string | null;
  createdAt: string;
}

/** Befund dazu, warum zu einem Ticket kein automatisches Briefing existiert. */
export interface KianAnalysisDiagnosis {
  code:
    | 'analysis_exists'
    | 'queue_unavailable'
    | 'job_pending'
    | 'job_failed'
    | 'job_stuck_no_worker'
    | 'job_completed_without_analysis'
    | 'not_mirrored'
    | 'ticket_too_old'
    | 'customer_hourly_limit'
    | 'daily_limit'
    | 'not_new_to_mirror';
  message: string;
  detail?: string;
  manualPossible: boolean;
  counters: {
    customerLastHour: number;
    customerHourlyLimit: number;
    globalLast24h: number;
    globalDailyLimit: number;
  };
}
